import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Trophy, ArrowLeft, Search, MapPin, Calendar, Users, DollarSign,
  Heart, Briefcase, Filter, ExternalLink, Loader2, Send, ChevronDown
} from 'lucide-react';

const SPONSORSHIP_LEVELS = [
  'Any', 'Title', 'Presenting', 'Gold', 'Silver', 'Hole', 'Longest Drive',
  'Closest to the Pin', 'Beverage Cart', 'Breakfast', 'Lunch', 'Dinner',
  'Gift Bag', 'Prize', 'Driving Range', 'Putting Contest', 'Product', 'Custom'
];

const STATES = ['', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

export default function SponsorMarketplace() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: '', event_type: '', level: '', max_cost: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [requestModal, setRequestModal] = useState(null); // event obj
  const [requestForm, setRequestForm] = useState({ level: '', message: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.state) qs.set('state', filters.state);
      if (filters.event_type) qs.set('event_type', filters.event_type);
      if (filters.level && filters.level !== 'Any') qs.set('level', filters.level);
      if (filters.max_cost) qs.set('max_cost', filters.max_cost);
      const res = await fetch(`${API}/api/sponsors/marketplace?${qs.toString()}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e) {
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [filters.state, filters.event_type, filters.level, filters.max_cost]);

  const submitRequest = async () => {
    if (!user) {
      toast.error('Please sign in as a sponsor first');
      navigate('/sponsors');
      return;
    }
    setSubmittingRequest(true);
    try {
      const res = await fetch(`${API}/api/sponsors/events/${requestModal.event_id}/request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Request sent! The organizer will be in touch.');
        setRequestModal(null);
        setRequestForm({ level: '', message: '' });
      } else {
        toast.error(data?.detail || 'Failed to send request');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const cheapest = (levels) => {
    const costs = (levels || []).map(l => Number(l.cost || 0)).filter(x => x > 0);
    return costs.length ? Math.min(...costs) : null;
  };

  return (
    <div className="min-h-screen bg-bundle-cream" data-testid="sponsor-marketplace">
      {/* Header */}
      <div className="bg-bundle-green text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/sponsors')} className="text-bundle-gold hover:underline text-xs mb-4 inline-flex items-center gap-1" data-testid="marketplace-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Sponsor Info
          </button>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-bundle-gold">Sponsor Marketplace</span>
              <h1 className="text-3xl sm:text-4xl font-black mt-1">Find Events Seeking Sponsors</h1>
              <p className="text-sm text-white/70 mt-1">Browse live opportunities and request sponsorship directly from organizers.</p>
            </div>
            <Button onClick={() => setShowFilters(!showFilters)} data-testid="marketplace-filter-toggle" variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Filter className="w-4 h-4 mr-2" /> Filters <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border-b border-bundle-cream-dark" data-testid="marketplace-filters-panel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-600">State</Label>
              <select data-testid="filter-state" value={filters.state} onChange={e => setFilters({ ...filters, state: e.target.value })} className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm bg-white">
                {STATES.map(s => <option key={s} value={s}>{s || 'Any State'}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Event Type</Label>
              <select data-testid="filter-event-type" value={filters.event_type} onChange={e => setFilters({ ...filters, event_type: e.target.value })} className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm bg-white">
                <option value="">Any</option>
                <option value="fundraiser">Fundraiser / Charity</option>
                <option value="tournament">Tournament</option>
                <option value="social">Corporate / Social</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Sponsorship Level</Label>
              <select data-testid="filter-level" value={filters.level} onChange={e => setFilters({ ...filters, level: e.target.value })} className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm bg-white">
                {SPONSORSHIP_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Max Budget ($)</Label>
              <Input data-testid="filter-max-cost" type="number" value={filters.max_cost} onChange={e => setFilters({ ...filters, max_cost: e.target.value })} placeholder="Any" className="mt-1 h-9" />
            </div>
          </div>
        </div>
      )}

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-bundle-green mx-auto" /></div>
        ) : events.length === 0 ? (
          <div className="bg-white border border-bundle-cream-dark rounded-2xl p-10 text-center" data-testid="marketplace-empty-state">
            <Briefcase className="w-12 h-12 text-bundle-cream-dark mx-auto mb-3" />
            <h3 className="text-lg font-bold text-bundle-green mb-2">No matching opportunities yet</h3>
            <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or check back soon — new tournaments are added weekly.</p>
            {!user && (
              <Button onClick={() => navigate('/sponsors')} className="bg-blue-500 hover:bg-blue-400 text-white font-bold">Become a Sponsor</Button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4" data-testid="marketplace-count">{events.length} event{events.length !== 1 ? 's' : ''} seeking sponsors</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map(ev => {
                const min = cheapest(ev.sponsorship_levels);
                return (
                  <div key={ev.event_id} data-testid={`marketplace-event-${ev.event_id}`} className="bg-white border border-bundle-cream-dark rounded-2xl overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
                    {ev.flyer_image && (
                      <div className="h-40 bg-bundle-cream-dark relative overflow-hidden">
                        <img src={ev.flyer_image} alt={ev.title} className="w-full h-full object-cover" />
                        {ev.is_fundraiser && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1"><Heart className="w-3 h-3" /> Fundraiser</span>
                        )}
                      </div>
                    )}
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-base font-bold text-bundle-green mb-1">{ev.title}</h3>
                      <div className="space-y-1 text-xs text-gray-500 mb-3">
                        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {ev.date} {ev.time && `· ${ev.time}`}</div>
                        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {ev.location}</div>
                        {ev.expected_attendance && <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {ev.expected_attendance} attendees</div>}
                        {ev.charity_info && <div className="flex items-center gap-1.5 text-amber-700"><Heart className="w-3.5 h-3.5" /> {ev.charity_info}</div>}
                      </div>

                      {ev.sponsorship_levels && ev.sponsorship_levels.length > 0 && (
                        <div className="bg-bundle-cream/50 rounded-lg p-3 mb-3">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-bundle-gold-dark mb-1.5">Available Sponsorships</p>
                          <div className="space-y-1">
                            {ev.sponsorship_levels.slice(0, 4).map((lvl, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-bundle-green font-semibold">{lvl.name}</span>
                                <span className="text-gray-600">{lvl.cost ? `$${Number(lvl.cost).toLocaleString()}` : 'Contact'}</span>
                              </div>
                            ))}
                            {ev.sponsorship_levels.length > 4 && <p className="text-[10px] text-gray-400">+ {ev.sponsorship_levels.length - 4} more</p>}
                          </div>
                        </div>
                      )}

                      {min && (
                        <div className="text-xs text-gray-500 mb-3">Starting at <span className="font-bold text-bundle-green">${min.toLocaleString()}</span></div>
                      )}

                      <div className="flex gap-2 mt-auto">
                        <Button onClick={() => navigate(`/events?event=${ev.event_id}`)} variant="outline" className="flex-1 text-xs" data-testid={`marketplace-details-${ev.event_id}`}>
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Details
                        </Button>
                        <Button onClick={() => { setRequestModal(ev); setRequestForm({ level: '', message: '' }); }} className="flex-1 bg-blue-500 hover:bg-blue-400 text-white text-xs" data-testid={`marketplace-request-${ev.event_id}`}>
                          <Send className="w-3.5 h-3.5 mr-1.5" /> Request Sponsorship
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Request Modal */}
      <Dialog open={!!requestModal} onOpenChange={(o) => !o && setRequestModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Sponsorship</DialogTitle>
          </DialogHeader>
          {requestModal && (
            <div className="space-y-4 py-2">
              <div className="bg-bundle-cream/50 border-l-4 border-bundle-gold rounded-lg p-3">
                <p className="font-bold text-sm text-bundle-green">{requestModal.title}</p>
                <p className="text-xs text-gray-500">{requestModal.date} · {requestModal.location}</p>
              </div>
              <div>
                <Label className="text-xs">Level of Interest</Label>
                <select data-testid="request-level" value={requestForm.level} onChange={e => setRequestForm({ ...requestForm, level: e.target.value })} className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm bg-white">
                  <option value="">Any / To be discussed</option>
                  {(requestModal.sponsorship_levels || []).map((lvl, i) => (
                    <option key={i} value={lvl.name}>{lvl.name}{lvl.cost ? ` · $${Number(lvl.cost).toLocaleString()}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Message to Organizer (optional)</Label>
                <Textarea data-testid="request-message" value={requestForm.message} onChange={e => setRequestForm({ ...requestForm, message: e.target.value })} rows={4} placeholder="Introduce your business and let the organizer know what you're hoping to sponsor…" className="mt-1" />
              </div>
              {!user && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  You need to be signed in as a sponsor to submit a request. <a href="/sponsors" className="underline font-bold">Apply here</a>.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestModal(null)}>Cancel</Button>
            <Button data-testid="request-submit-btn" onClick={submitRequest} disabled={submittingRequest || !user} className="bg-blue-500 hover:bg-blue-400 text-white">
              {submittingRequest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Request</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
